import React, {useState, useEffect, useRef} from 'react';
import {Box, Flex, Button, Heading, Divider, Spacer} from '@chakra-ui/react';
import {useForm} from 'react-hook-form';
import CriteriaCard from '../../components/Reports/CriteriaCard';
import FlexCard from '../../components/General/FlexCard';
import BoxCard from '../../components/General/BoxCard';
import {CustomInput, SubmitButton, CustomCheckbox, CustomRadioGroup, DatePicker} from '../../components/General/FormInputs';
import LinkButton from '../../components/General/LinkButton';
import {ClientSelect} from '../../components/Client/ClientSelect';
import Visibility from '../../constants/Visibility';
import SurveyTypesReports from '../../components/Reports/SurveyTypesReports';
import FormCodes from '../../constants/FormCodes';
import {get, getReport} from '../../service/ApiService';
import {Report, User} from '../../service/Urls';
import ErrorHelper from '../../service/ErrorHelper';
import {ArrangerSelect} from '../../components/Arranger/ArrangerSelect';
import {GroupSelect} from '../../components/Group/GroupSelect';

import LoadingWheel from '../../components/General/LoadingWheel';
import {LocationSelect} from '../../components/Location/LocationSelect';
import {useTitle} from '../../components/General/Layout';
import Roles from '../../constants/Roles';
import {getUserInfo, isAdmin, isClientPersonnelOnly, roleContains} from '../../helpers/localStorage';
import {ReactClientSelect} from '../../components/Client/ReactClientSelect';

const ClientReports = () => {
  // use setTitle to update the title of the page, otherwise set in the router typically
  const [title, setTitle] = useTitle('');

  // current selected report
  const [reportVal, setReportVal] = useState('');

  // current selected radio button id, needed for radio control
  const [promptId, setPromptId] = useState('');

  // list of report data
  const [sectionList, setSectionsList] = useState('');

  // current service types to show
  const [serviceTypes, setServiceTypes] = useState([]);

  // currently selected service type
  const [serviceTypeValue, setServiceTypeValue] = useState('');

  // if they are a client personnel only user
  const [isClient, setIsClient] = useState(false);
  const [defaultClient, setDefaultClient] = useState('');
  const [userData, setUserData] = useState(null);
  const [sectionId, setSectionId] = useState();
  const [toastResponse, setToast] = useState('');
  const [isInitialLoading, setInitialLoading] = useState(true);
  const [isLoading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState('');
  const linkRef = useRef(null);
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    watch,
    formState: {errors},
  } = useForm();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (Array.isArray(serviceTypes)) {
      if (serviceTypes.includes(serviceTypeValue)) {
        return;
      } else {
        setServiceTypeValue(serviceTypes[0]);
      }
    }
  }, [serviceTypes]);

  useEffect(() => {
    if (Array.isArray(sectionList)) {
      sectionList.forEach(section => {
        const obj = section.reports.find(obj => {
          return obj.promptId === promptId;
        });

        if (typeof obj !== 'undefined') {
          setReportVal(obj);
        }
      });
    }
  }, [promptId]);

  // When the report changes, zero out records that should be disabled - select searches are handled in their own components
  useEffect(() => {
    defaultDates();
  }, [reportVal]);

  const defaultDates = () => {
    const formValues = getValues();
    const Today = new Date().toISOString().split('T')[0];
    if (reportVal.hasOwnProperty('startDateState')) setValue('startDate', formValues.startDate || Today);
    if (reportVal.hasOwnProperty('startDate2State')) setValue('startDate2', formValues.startDate2 || Today);
    if (reportVal.hasOwnProperty('endDateState')) setValue('endDate', formValues.endDate || Today);
    if (reportVal.hasOwnProperty('endDate2State')) setValue('endDate2', formValues.endDate2 || Today);
  };

  const filterFields = data => {
    if (!reportVal.hasOwnProperty('secondaryArrangerState')) data.secondaryArranger = false;
    if (!reportVal.hasOwnProperty('startDateState')) data.startDate = '';
    if (!reportVal.hasOwnProperty('startDate2State')) data.startDate2 = '';
    if (!reportVal.hasOwnProperty('endDateState')) data.endDate = '';
    if (!reportVal.hasOwnProperty('endDate2State')) data.endDate2 = '';
    if (!reportVal.hasOwnProperty('atPreNeedState')) data.atPreNeed = '';
    if (!reportVal.hasOwnProperty('numMosDashboardState')) data.numMosDashboard = '';
    if (!reportVal.hasOwnProperty('scanPhraseState')) data.scanPhrase = '';
    if (!reportVal.hasOwnProperty('preNeedFunCremState')) data.preNeedFunCrem = '';
  };

  // once the pdf url is loaded click the hidden link to open a new tab
  useEffect(() => {
    if (pdfUrl) linkRef.current.click();
  }, [pdfUrl]);

  // This will filter out sections if the user is ONLY a client personnel and does not have access to certain reports. set this in user maintenance
  // This also sets the default value for the client select
  const filterClientCards = async sections => {
    if (isAdmin() || !roleContains(Roles.CLIENT_PERSONNEL)) {
      setUserData({});
      return sections;
    }
    // get user info
    const userInfo = getUserInfo();
    const userRecord = await get(User.USER + `/` + userInfo.nameid);
    setUserData(userRecord);

    // filter
    if (!userRecord.canSeeOppPlanReports) sections = sections.filter(section => section.section !== 'Opportunity Plan Reports');
    if (!userRecord.canSeePivotTableReports) sections = sections.filter(section => section.section !== 'Pivot Tables');
    if (!userRecord.canSeeRevenueReports) sections = sections.filter(section => section.section !== 'Revenue Reports');

    const oppPlanSection = sections.find(section => section.section === 'Opportunity Plan Reports'); // section must exist to filter individual reports in the opp plan section
    if (oppPlanSection) {
      if (!userRecord.canSeeOppPlanSetupReport) {
        oppPlanSection.reports = oppPlanSection.reports.filter(report => report.description !== 'Opp Plan Setup');
      }
      if (!userRecord.canSeeManagerPerformanceReview) {
        oppPlanSection.reports = oppPlanSection.reports.filter(report => report.description !== 'Performance Review Manager');
      }
      if (!userRecord.canSeeArrangerPerformanceReview) {
        oppPlanSection.reports = oppPlanSection.reports.filter(report => report.description !== 'Performance Review Arranger');
      }
    }

    return sections;
  };

  const loadData = async () => {
    setIsClient(isClientPersonnelOnly());

    if (isClientPersonnelOnly()) {
      const user = await get(User.USER + `/` + getUserInfo().nameid);

      const client = user?.clients[0];

      if (client) {
        setDefaultClient(client.clId);
        setValue('clientId', client.id);
      }
    }
    const data = await get(Report.LIST + `/` + FormCodes.CLIENT_REPORTS);

    setToast(data);
    if (!data.error) {
      const sections = data.data;
      if (sections) {
        setPromptId(sections[0].reports[0].promptId);
        const filteredSections = await filterClientCards(sections);
        setSectionsList(filteredSections);
        setSectionId(sections[0].sectionId);
        setReportVal(sections[0]?.reports[0]);
        setServiceTypes(sections[0]?.reports[0]?.serviceTypes);
      }
    }
    setInitialLoading(false);
    setLoading(false);
    setTitle('Client Reports');
  };

  // for the report options
  const radios = {
    needs: [
      {key: 'At Need', value: 'atNeed'},
      {key: 'PreNeed', value: 'preNeed'},
      {key: 'Both', value: 'both'},
    ],

    forPreNeed: [
      {key: 'Funeral', value: 'funeral'},
      {key: 'Cremation', value: 'cremation'},
    ],
  };

  const printReport = async data => {
    setLoading(true);
    data.formCode = FormCodes.CLIENT_REPORTS;
    data.serviceType = serviceTypeValue;
    data.sectionId = sectionId;
    data.promptId = reportVal.promptId;
    if (data.importAnalysis) data[data.importAnalysis] = true;

    // will return url if successful, object with error field if not
    filterFields(data);
    const reportResponse = await getReport(data);

    if (reportResponse) {
      if (reportResponse.error) {
        setToast(reportResponse);
      } else {
        setPdfUrl(reportResponse);
      }
    }
    setLoading(false);
    // const reportParams = {name: 'ClientId', values: [data.clientId]};
    // navigate('/reports/viewer', {state: {id: 1, reportName: 'GAI Secondary Arrangers_Secarr.rdlx', reportParams: reportParams}});
  };

  const ReportCards = () => {
    const col1 = [];
    const col2 = [];
    for (let i = 0; i < sectionList.length; i++) {
      const sec = sectionList[i];
      if (i < 2) {
        col1.push(
          <CriteriaCard value={promptId} setValue={setPromptId} reportInfo={sec} key={sec.sectionId} setSectionId={setSectionId}>
            reports
          </CriteriaCard>
        );
      } else {
        col2.push(
          <CriteriaCard value={promptId} setValue={setPromptId} reportInfo={sec} key={sec.sectionId} setSectionId={setSectionId}>
            reports
          </CriteriaCard>
        );
      }
    }
    return (
      <>
        <Box m="3">
          {col1}
          <FlexCard>
            <CustomCheckbox
              name="Secondary Arrangers"
              id="isSecondaryArranger"
              control={control}
              errors={errors}
              required={reportVal?.secondaryArrangerState === Visibility.REQUIRED}
              disabled={!reportVal.hasOwnProperty('secondaryArrangerState')}
            />
          </FlexCard>
        </Box>
        <Box m="3">
          {col2}
          <Box>
            {/* Service Types */}
            <FlexCard>
              <SurveyTypesReports serviceTypeValue={serviceTypeValue} setServiceTypeValue={setServiceTypeValue} serviceTypes={serviceTypes} />
            </FlexCard>
          </Box>
          {/* <FlexCard>
            <Button w={'350px'} colorScheme="secondary">
              Compensation Study
            </Button>
          </FlexCard> */}
        </Box>
      </>
    );
  };

  return (
    <>
      <a href={pdfUrl} target="_blank" rel="noopener noreferrer" ref={linkRef} style={{display: 'none'}} />
      {isInitialLoading ? (
        <LoadingWheel />
      ) : (
        <form onSubmit={handleSubmit(printReport)}>
          <Flex flexDir={{base: 'column', lg: 'row'}}>
            {/* things to the left of report options */}
            <ReportCards />

            {/* Report Options */}
            <Box mt="3">
              <BoxCard>
                <Heading>Report Options</Heading>
                <Divider my="3" />
                {userData !== null && (
                  <ReactClientSelect
                    errors={errors}
                    id={'clientCode'}
                    required={isClientPersonnelOnly() ? true : reportVal?.clientIdState === Visibility.REQUIRED}
                    disabled={isClientPersonnelOnly() && userData?.clients.length === 1}
                    defaultValue={defaultClient}
                    useClientCode={true}
                    control={control}
                    userData={userData}
                    setClientId={id => setValue('clientId', id)}
                  />
                )}
                <Divider my="2" />
                <Flex>
                  <DatePicker
                    register={register}
                    errors={errors}
                    name="Starting Date"
                    id="startDate"
                    required={reportVal?.startDateState === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('startDateState')}
                  />
                  <DatePicker
                    register={register}
                    errors={errors}
                    name="Starting Date 2"
                    id="startDate2"
                    required={reportVal?.startDate2State === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('startDate2State')}
                  />
                </Flex>
                <Flex>
                  <DatePicker
                    register={register}
                    errors={errors}
                    name="Ending Date"
                    id="endDate"
                    required={reportVal?.startDateState === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('endDateState')}
                  />
                  <DatePicker
                    register={register}
                    errors={errors}
                    name="Ending Date 2"
                    id="endDate2"
                    required={reportVal?.endDate2State === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('endDate2State')}
                  />
                </Flex>

                <Divider my="2" />
                <Heading size="md" mb="1">
                  Used for CFS Summary Report
                </Heading>
                <CustomRadioGroup
                  list={radios.needs}
                  control={control}
                  errors={errors}
                  id="atPreneedBoth"
                  defaultValue={!reportVal.hasOwnProperty('atPreNeedState') ? '0' : 'both'}
                  required={reportVal?.atPreNeedState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('atPreNeedState')}
                />

                <Divider my="2" />
                <GroupSelect
                  register={register}
                  errors={errors}
                  id={'groupId'}
                  required={reportVal?.groupState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('groupState')}
                  setRHFValue={setValue}
                  setId
                />

                <Divider my="2" />
                <LocationSelect
                  register={register}
                  errors={errors}
                  id={'locationId'}
                  required={reportVal?.locationState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('locationState')}
                  setRHFValue={setValue}
                  clientId={watch('clientId')}
                />

                <Divider my="2" />
                <ArrangerSelect
                  register={register}
                  errors={errors}
                  id={'arrangerId'}
                  required={reportVal?.arrangerState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('arrangerState')}
                  setRHFValue={setValue}
                  clientId={watch('clientId')}
                />

                <Divider my="2" />
                <CustomInput
                  id="numMos4Dashboard"
                  register={register}
                  errors={errors}
                  name="No. of Months for Dashboard Report"
                  required={reportVal?.numMosDashboardState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('numMosDashboardState')}
                />

                <Divider my="2" />
                <CustomInput
                  id="scanForPhrase"
                  register={register}
                  errors={errors}
                  name="Scan For:"
                  required={reportVal?.scanPhraseState === Visibility.REQUIRED}
                  disabled={!reportVal.hasOwnProperty('scanPhraseState')}
                />

                <Divider my="2" />
                <Flex>
                  <Heading size="sm">For PreNeed Reports:</Heading>
                  <CustomRadioGroup
                    list={radios.forPreNeed}
                    control={control}
                    errors={errors}
                    id="preneedRevFunCremBoth"
                    defaultValue={!reportVal.hasOwnProperty('preNeedFunCremState') ? '0' : 'funeral'}
                    required={reportVal?.preNeedFunCremState === Visibility.REQUIRED}
                    disabled={!reportVal.hasOwnProperty('preNeedFunCremState')}
                  />
                </Flex>

                <Divider my="3" />

                {isLoading ? (
                  <Flex justify={'center'}>
                    <LoadingWheel />
                  </Flex>
                ) : (
                  <>
                    <Flex>
                      <Box w="45%">
                        <LinkButton to="/" label="Back" />
                      </Box>
                      <Spacer />
                      <Box w="45%">
                        <SubmitButton name="Print" />
                      </Box>
                    </Flex>
                  </>
                )}
              </BoxCard>
            </Box>
          </Flex>

          <ErrorHelper toastResponse={toastResponse} />
        </form>
      )}
    </>
  );
};

export default ClientReports;
